import { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Container, Grid, Card, Image, Header, Loader, Dimmer, Label,
    Button, Message, Input, Icon, Dropdown,
} from 'semantic-ui-react';
import moment from 'moment';
import { globalSettings } from './lib/configuration';
import { setSEO, setJSONLD, clearJSONLD } from './lib/seo';
import { useSavedEvents } from './lib/savedEventsStore';
import { t } from './locales';
import { formatMoney } from './lib/currency';
import { withPlatform } from './lib/onlinePlatforms';

const API = globalSettings.PORTAL_API_URL;

const getDirectoryEvents = async ({ sort, category, location, online, free, organiser, page, page_size }) => {
    const params = new URLSearchParams();
    if (sort && sort !== 'date') params.set('sort', sort);
    if (category) params.set('category', category);
    if (location) params.set('location', location);
    if (online !== null) params.set('online', online ? 'true' : 'false');
    if (free) params.set('free', 'true');
    if (organiser) params.set('organiser', organiser);
    if (page > 1) params.set('page', page);
    if (page_size !== 24) params.set('page_size', page_size);
    const qs = params.toString();
    const res = await fetch(`${API}/api/v1/ticketing/directory/events/${qs ? `?${qs}` : ''}`);
    return res.json();
};

const getSortOptions = () => [
    { key: 'date', text: t('directory.sortUpcoming'), value: 'date' },
    { key: 'price', text: t('directory.sortPriceLowHigh'), value: 'price' },
    { key: 'name', text: t('directory.sortNameAZ'), value: 'name' },
];

const PURPLE = '#481b52';
const GREEN = '#a4cf58';
const PAGE_SIZE = 24;

function FilterChip({ active, onClick, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                cursor: 'pointer',
                backgroundColor: active ? PURPLE : '#e8e8e8',
                color: active ? '#fff' : '#333',
                borderRadius: 20,
                padding: '6px 14px',
                fontWeight: active ? 600 : 400,
                transition: 'all 0.15s',
                border: 'none',
                fontSize: '0.85rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                lineHeight: 1.4,
            }}
        >
            {children}
        </button>
    );
}

function HeartButton({ eventId, style = {} }) {
    const { isSaved, toggle } = useSavedEvents();
    const saved = isSaved(eventId);
    return (
        <button
            onClick={(e) => { e.stopPropagation(); toggle(eventId); }}
            title={saved ? t('common.removeFromSaved') : t('common.saveEvent')}
            style={{
                position: 'absolute', top: 10, right: isSaved ? 10 : 10,
                background: 'rgba(255,255,255,0.9)',
                border: 'none', borderRadius: '50%',
                width: 32, height: 32,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', zIndex: 2,
                boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                ...style,
            }}
        >
            <Icon
                name={saved ? 'heart' : 'heart outline'}
                style={{ color: saved ? '#e74c3c' : '#888', margin: 0, fontSize: '1rem' }}
            />
        </button>
    );
}

function EventCard({ event, onOrgClick, onEventClick }) {
    const remaining = event.next_instance?.tickets_remaining;
    const isSoldOut = remaining === 0;
    const isLowStock = remaining != null && remaining > 0 && remaining <= 10;
    const isFree = parseFloat(event.ticket_price) === 0;

    return (
        <Card
            fluid
            className="eventCard"
            onClick={() => onEventClick(event)}
            style={{ borderRadius: 12, overflow: 'hidden', cursor: 'pointer', opacity: isSoldOut ? 0.7 : 1, position: 'relative' }}
        >
            <div style={{ position: 'relative' }}>
                {event.image_url ? (
                    <Image src={event.image_url} wrapped ui={false} style={{ height: 180, objectFit: 'cover', width: '100%' }} />
                ) : (
                    <div style={{ height: 180, background: `linear-gradient(135deg, ${PURPLE}, #7b3f8a)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '3rem' }}>🎟</span>
                    </div>
                )}

                <HeartButton eventId={event.id} />

                {isSoldOut && (
                    <div style={{ position: 'absolute', top: 10, left: 10, backgroundColor: 'rgba(0,0,0,0.65)', color: '#fff', borderRadius: 6, padding: '3px 10px', fontSize: '0.8rem', fontWeight: 700 }}>{t('common.soldOut')}</div>
                )}
                {isLowStock && (
                    <div style={{ position: 'absolute', top: 10, left: 10, backgroundColor: '#e67e22', color: '#fff', borderRadius: 6, padding: '3px 10px', fontSize: '0.8rem', fontWeight: 700 }}>{t('common.ticketsLeft', { remaining })}</div>
                )}
                {event.online_event && (
                    <div style={{ position: 'absolute', bottom: 34, left: 8, backgroundColor: 'rgba(52,152,219,0.9)', color: '#fff', borderRadius: 6, padding: '3px 10px', fontSize: '0.75rem', fontWeight: 600 }}>
                        <Icon name="wifi" style={{ margin: 0 }} /> {withPlatform(t('common.online'), event.online_platform)}
                    </div>
                )}
                <div
                    onClick={(e) => { e.stopPropagation(); onOrgClick(event.instance_slug); }}
                    style={{ position: 'absolute', bottom: 8, left: 8, backgroundColor: 'rgba(72,27,82,0.88)', color: '#fff', borderRadius: 4, padding: '3px 8px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'rgba(255,255,255,0.4)' }}
                >
                    {event.organiser_logo
                        ? <img src={event.organiser_logo} alt="" style={{ height: 14, width: 'auto', borderRadius: 2 }} />
                        : <Icon name="building outline" style={{ margin: 0, fontSize: '0.7rem' }} />}
                    {event.organiser_name}
                </div>
            </div>

            <Card.Content>
                <Card.Header style={{ color: isSoldOut ? '#999' : PURPLE }}>{event.name}</Card.Header>
                {event.next_instance?.date_time && (
                    <Card.Meta><Icon name="calendar outline" />{moment(event.next_instance.date_time).format('ddd D MMM YYYY, HH:mm')}</Card.Meta>
                )}
                {event.location && <Card.Meta><Icon name="map marker alternate" />{event.location}</Card.Meta>}
                {event.category && (
                    <div style={{ marginTop: '0.4rem' }}>
                        <Label size="tiny" style={{ backgroundColor: PURPLE, color: '#fff' }}>{event.category}</Label>
                    </div>
                )}
                <Card.Description style={{ marginTop: '0.5rem', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                    {event.description}
                </Card.Description>
            </Card.Content>

            <Card.Content extra style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Label style={{ backgroundColor: isSoldOut ? '#ccc' : isFree ? '#27ae60' : GREEN, color: '#fff', fontWeight: 700 }}>
                    {isSoldOut ? t('common.soldOut') : isFree ? t('common.free') : formatMoney(event.ticket_price, event.currency)}
                </Label>
                {event.instance_count > 1 && <span style={{ fontSize: '0.85rem', color: '#888' }}>{t('common.datesCount', { count: event.instance_count })}</span>}
                <Button size="small" style={{ backgroundColor: isSoldOut ? '#ccc' : PURPLE, color: '#fff' }}>
                    {isSoldOut ? t('common.view') : t('common.getTickets')}
                </Button>
            </Card.Content>
        </Card>
    );
}

function DirectoryEventList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { savedIds, count: savedCount } = useSavedEvents();

    const sort = searchParams.get('sort') || 'date';
    const category = searchParams.get('category') || '';
    const location = searchParams.get('location') || '';
    const onlineRaw = searchParams.get('online');
    const online = onlineRaw === 'true' ? true : onlineRaw === 'false' ? false : null;
    const free = searchParams.get('free') === 'true';
    const showSaved = searchParams.get('saved') === '1';
    const page = parseInt(searchParams.get('page') || '1', 10);
    const [search, setSearch] = useState(searchParams.get('q') || '');
    const [allEvents, setAllEvents] = useState([]);
    const [metaCache, setMetaCache] = useState({});

    const setParam = useCallback((key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value) next.set(key, value);
        else next.delete(key);
        next.delete('page');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const setPage = useCallback((p) => {
        const next = new URLSearchParams(searchParams);
        if (p > 1) next.set('page', p);
        else next.delete('page');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const { data, isLoading, isError } = useQuery({
        queryKey: ['directory-events', sort, category, location, onlineRaw, free, page],
        queryFn: () => getDirectoryEvents({ sort, category, location, online, free, page, page_size: PAGE_SIZE }),
        enabled: !showSaved,
    });

    useEffect(() => {
        if (page === 1) {
            // Filter changed or fresh load — replace (including clearing when data is undefined)
            setAllEvents(data?.events ?? []);
        } else if (data?.events) {
            // Load more — append, deduplicating by id+instance
            setAllEvents(prev => {
                const existing = new Set(prev.map(e => `${e.id}-${e.instance_id || ''}`));
                const newOnes = data.events.filter(e => !existing.has(`${e.id}-${e.instance_id || ''}`));
                return [...prev, ...newOnes];
            });
        }
        if (data?.meta) setMetaCache(data.meta);
    }, [data, page, sort, category, location, onlineRaw, free]);

    const meta = data?.meta ?? metaCache;
    const hasMore = meta.total_pages > page;

    const visibleEvents = useMemo(() => {
        let base = showSaved
            ? [...savedIds].map(id => allEvents.find(e => e.id === id)).filter(Boolean)
            : allEvents;
        if (!search) return base;
        const q = search.toLowerCase();
        return base.filter(e =>
            e.name.toLowerCase().includes(q) ||
            (e.description || '').toLowerCase().includes(q) ||
            (e.location || '').toLowerCase().includes(q) ||
            (e.organiser_name || '').toLowerCase().includes(q)
        );
    }, [allEvents, showSaved, savedIds, search]);

    const handleEventClick = (event) => {
        const url = event.instance_id
            ? `/i/${event.instance_slug}/events/${event.id}?instance=${event.instance_id}`
            : `/i/${event.instance_slug}/events/${event.id}`;
        window.location.href = url;
    };

    const handleOrgClick = (slug) => navigate(`/directory/organisers/${slug}`);

    const locationOptions = useMemo(() =>
        [{ key: '', text: t('directory.allLocations'), value: '' },
         ...(meta.locations || []).map(l => ({ key: l, text: l, value: l }))],
        [meta.locations]
    );

    // SEO
    useEffect(() => {
        setSEO({
            title: t('directory.seoTitle'),
            description: t('directory.seoDescription'),
            url: window.location.href,
        });
        if (allEvents.length > 0) {
            setJSONLD({
                '@context': 'https://schema.org',
                '@type': 'ItemList',
                name: t('directory.jsonldListName'),
                description: t('directory.jsonldListDescription'),
                itemListElement: allEvents.slice(0, 20).map((e, i) => ({
                    '@type': 'ListItem',
                    position: i + 1,
                    item: {
                        '@type': 'Event',
                        name: e.name,
                        description: e.description || '',
                        ...(e.next_instance?.date_time ? { startDate: e.next_instance.date_time } : {}),
                        ...(e.online_event ? { eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode' } : {}),
                        location: e.online_event ? { '@type': 'VirtualLocation' } : { '@type': 'Place', name: e.location },
                        offers: { '@type': 'Offer', price: e.ticket_price, priceCurrency: e.currency || 'GBP', url: `${window.location.origin}/i/${e.instance_slug}/events/${e.id}` },
                        organizer: { '@type': 'Organization', name: e.organiser_name },
                        ...(e.image_url ? { image: e.image_url } : {}),
                    },
                })),
            });
        }
        return () => clearJSONLD();
    }, [allEvents]);

    if (isLoading && allEvents.length === 0) {
        return <Dimmer active inverted page><Loader size="large" content={t('directory.loadingEvents')} /></Dimmer>;
    }
    if (isError && allEvents.length === 0) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: PURPLE }}>
                <Container textAlign="center">
                    <Icon name="warning sign" size="huge" style={{ color: 'rgba(255,255,255,0.4)' }} />
                    <Header as="h2" inverted>{t('common.couldNotLoadEvents')}</Header>
                </Container>
            </div>
        );
    }

    const activeFilterCount = [category, location, online !== null, free].filter(Boolean).length;
    const sortOptions = getSortOptions();

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
            {/* Header */}
            <div style={{ backgroundColor: PURPLE, padding: '40px 20px 30px', textAlign: 'center' }}>
                <Icon name="ticket" size="large" style={{ color: 'rgba(255,255,255,0.5)', marginBottom: 8 }} />
                <Header as="h1" inverted style={{ fontSize: 28, fontWeight: 300, marginTop: 0, marginBottom: 6 }}>
                    {t('directory.title')}
                </Header>
                <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: 24 }}>
                    {meta.total != null
                        ? (meta.total === 1
                            ? t('directory.eventsCountFromAllOne')
                            : t('directory.eventsCountFromAllOther', { count: meta.total }))
                        : t('directory.browseEventsFromAll')}
                </p>
                <div style={{ maxWidth: 520, margin: '0 auto' }}>
                    <Input fluid icon="search" iconPosition="left" placeholder={t('directory.searchPlaceholder')}
                        value={search} onChange={e => setSearch(e.target.value)} style={{ borderRadius: 8 }} />
                </div>
            </div>

            <Container style={{ padding: '20px 0 60px' }}>
                {/* Filter bar */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
                    <Dropdown selection compact options={sortOptions} value={sort}
                        onChange={(_, { value }) => setParam('sort', value === 'date' ? '' : value)}
                        style={{ minWidth: 160 }} />

                    <FilterChip active={online === true} onClick={() => setParam('online', online === true ? '' : 'true')}>
                        <Icon name="wifi" />{t('common.online')}
                    </FilterChip>
                    <FilterChip active={online === false} onClick={() => setParam('online', online === false ? '' : 'false')}>
                        <Icon name="map marker alternate" />{t('directory.inPerson')}
                    </FilterChip>
                    <FilterChip active={free} onClick={() => setParam('free', free ? '' : 'true')}>
                        <Icon name="tag" />{t('common.free')}
                    </FilterChip>
                    <FilterChip active={showSaved} onClick={() => setParam('saved', showSaved ? '' : '1')}>
                        <Icon name="heart" style={{ color: showSaved ? '#fff' : '#e74c3c' }} />
                        {t('directory.saved')}{savedCount() > 0 ? ` (${savedCount()})` : ''}
                    </FilterChip>

                    {locationOptions.length > 1 && (
                        <Dropdown selection compact options={locationOptions} value={location}
                            onChange={(_, { value }) => setParam('location', value)}
                            placeholder={t('directory.locationPlaceholder')} style={{ minWidth: 160 }} />
                    )}

                    {activeFilterCount > 0 && (
                        <Button basic size="mini" onClick={() => {
                            const next = new URLSearchParams();
                            if (sort !== 'date') next.set('sort', sort);
                            setSearchParams(next, { replace: true });
                        }}>
                            {t('directory.clearFilters', { count: activeFilterCount })}
                        </Button>
                    )}
                </div>

                {/* Category pills */}
                {(meta.categories || []).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1.25rem' }}>
                        <FilterChip active={!category} onClick={() => setParam('category', '')}>{t('directory.categoryAll')}</FilterChip>
                        {(meta.categories || []).map(cat => (
                            <FilterChip key={cat} active={category === cat} onClick={() => setParam('category', category === cat ? '' : cat)}>
                                {cat}
                            </FilterChip>
                        ))}
                    </div>
                )}

                {/* Results */}
                {visibleEvents.length === 0 ? (
                    <Message info>
                        <Message.Header>{showSaved ? t('directory.noSavedEvents') : t('directory.noEventsFound')}</Message.Header>
                        <p>{showSaved
                            ? t('directory.noSavedEventsBody')
                            : search || activeFilterCount > 0
                                ? t('directory.noEventsFoundFilteredBody')
                                : t('directory.noEventsFoundBody')}
                        </p>
                    </Message>
                ) : (
                    <>
                        <Grid stackable columns={3}>
                            {visibleEvents.map(event => (
                                <Grid.Column key={event.instance_id ? `${event.id}-${event.instance_id}` : event.id}>
                                    <EventCard event={event} onEventClick={handleEventClick} onOrgClick={handleOrgClick} />
                                </Grid.Column>
                            ))}
                        </Grid>

                        {/* Pagination */}
                        {!showSaved && hasMore && (
                            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                                <Button
                                    loading={isLoading}
                                    onClick={() => setPage(page + 1)}
                                    style={{ backgroundColor: PURPLE, color: '#fff', minWidth: 160 }}
                                >
                                    {t('directory.loadMoreEvents')}
                                </Button>
                                <p style={{ marginTop: 8, color: '#888', fontSize: '0.85rem' }}>
                                    {t('directory.showingCount', { shown: visibleEvents.length, total: meta.total })}
                                </p>
                            </div>
                        )}
                    </>
                )}
            </Container>
        </div>
    );
}

export default DirectoryEventList;
